/**
 * useFileBrowser — the state behind both file dialogs: which workspace, which
 * location, the back history, search, type chips, view and sort, and the paged
 * listing for all of that.
 *
 * The dialog opens on the ACTING workspace (a host fact — the holder switcher's
 * selection, never something an arche names), or where the user last saved or
 * opened for this arche. The user — never the arche — may switch to another
 * workspace they can use for this dialog's purpose.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VaultFileDialogArea, VaultFileDialogDocumentDto } from '@/lib/api';
import { mimeToFileKind, type VaultFileKind } from '@/lib/vault-file-kinds';
import type { FileListEntry } from './FileList';
import {
  DOCUMENTS_ROOT,
  childFoldersAt,
  crumbsFor,
  listingFolderIdOf,
  parentLocationOf,
  sameLocation,
  type FileDialogLocation,
} from './locations';
import {
  readLastLocation,
  readSort,
  readView,
  writeLastLocation,
  writeSort,
  writeView,
  type FileDialogSort,
  type FileDialogView,
} from './prefs';
import {
  useDebounced,
  useFileDialogFolders,
  useFileDialogListing,
  useFileDialogWorkspaces,
  useThumbnails,
} from './useFileDialogData';

/**
 * What a requester accepts. Kinds are filtered server-side; MIME types
 * (`image/*` allowed) and extensions narrow further on the rows returned.
 */
export interface FileAccept {
  kinds?: readonly VaultFileKind[];
  mimeTypes?: readonly string[];
  extensions?: readonly string[];
}

export function acceptsFile(
  accept: FileAccept | undefined,
  file: Pick<VaultFileDialogDocumentDto, 'name' | 'mimeType'>,
): boolean {
  if (!accept) return true;
  const mime = (file.mimeType ?? '').toLowerCase().split(';')[0].trim();
  if (accept.kinds?.length && !accept.kinds.includes(mimeToFileKind(mime))) return false;
  const byMime = accept.mimeTypes?.length
    ? accept.mimeTypes.some((m) => {
        const want = m.toLowerCase();
        return want.endsWith('/*') ? mime.startsWith(want.slice(0, -1)) : mime === want;
      })
    : null;
  const lowerName = file.name.toLowerCase();
  const byExt = accept.extensions?.length
    ? accept.extensions.some((ext) =>
        lowerName.endsWith(ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`),
      )
    : null;
  // MIME types and extensions are alternatives ("PDF, or anything named .pdf").
  if (byMime === null && byExt === null) return true;
  return byMime === true || byExt === true;
}

export interface UseFileBrowserOptions {
  open: boolean;
  /** `read` lists every workspace the user can read; `write` only those they
   *  can save into. */
  need: 'read' | 'write';
  /** The areas this dialog browses. Save: Documents only. */
  areas: readonly VaultFileDialogArea[];
  /** The acting workspace; `null` = Personal. */
  initialHolderId: string | null;
  /** Remember the last location per user and this key (an arche id or a
   *  host surface name). Host-only — never sent to an arche. */
  memoryKey?: string;
  accept?: FileAccept;
  /** A further predicate on top of `accept` (legacy picker filters). */
  filter?: (file: VaultFileDialogDocumentDto) => boolean;
  /** Show files `accept` rejects, disabled, instead of hiding them. */
  showRejected?: boolean;
  /** A pick whose result an arche receives — never proprietary rows. */
  forArche?: boolean;
  /** Pin the workspace (a multi-select tray holds files of one workspace). */
  lockedHolderId?: string | null;
  /** Offer only the Personal workspace — for callers whose records can only
   *  hold personal files. */
  personalOnly?: boolean;
}

export function useFileBrowser(opts: UseFileBrowserOptions) {
  const {
    workspaces: allWorkspaces,
    principalId,
    isLoading: workspacesLoading,
  } = useFileDialogWorkspaces(opts.need);
  const memory = useMemo(
    () =>
      principalId && opts.memoryKey ? { userId: principalId, archeKey: opts.memoryKey } : undefined,
    [principalId, opts.memoryKey],
  );
  const workspaces = useMemo(
    () =>
      opts.personalOnly ? allWorkspaces.filter((w) => w.holderType === 'personal') : allWorkspaces,
    [allWorkspaces, opts.personalOnly],
  );
  const [holderId, setHolderIdState] = useState<string | null>(null);
  const [location, setLocation] = useState<FileDialogLocation>(DOCUMENTS_ROOT);
  const [history, setHistory] = useState<FileDialogLocation[]>([]);
  const [search, setSearch] = useState('');
  const [kinds, setKinds] = useState<VaultFileKind[]>([]);
  const [view, setViewState] = useState<FileDialogView>(readView);
  const [sort, setSortState] = useState<FileDialogSort>(readSort);
  const opened = useRef(false);

  // Resolve the starting workspace and location once per opening.
  useEffect(() => {
    if (!opts.open) {
      opened.current = false;
      return;
    }
    if (opened.current || workspaces.length === 0) return;
    opened.current = true;
    const usable = (id: string | null | undefined) =>
      !!id && workspaces.some((w) => w.holderId === id);
    const remembered = memory ? readLastLocation(memory.userId, memory.archeKey) : null;
    const acting = opts.initialHolderId ?? workspaces[0]?.holderId ?? null;
    if (
      remembered &&
      usable(remembered.holderId) &&
      opts.areas.includes(remembered.area) &&
      // The acting workspace wins over memory: working in an org, the dialog
      // opens there even if the last save for this arche went to Personal.
      (!opts.initialHolderId || remembered.holderId === opts.initialHolderId)
    ) {
      setHolderIdState(remembered.holderId);
      setLocation({ kind: 'folder', area: remembered.area, folderId: remembered.folderId });
    } else {
      setHolderIdState(usable(acting) ? acting : (workspaces[0]?.holderId ?? null));
      setLocation(DOCUMENTS_ROOT);
    }
    setHistory([]);
    setSearch('');
  }, [opts.open, workspaces, opts.initialHolderId, memory, opts.areas]);

  const foldersQuery = useFileDialogFolders(holderId);
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);

  // A remembered folder that no longer exists falls back to Documents.
  useEffect(() => {
    if (!foldersQuery.data || location.kind !== 'folder' || !location.folderId) return;
    if (!foldersQuery.data.some((f) => f.folderId === location.folderId)) {
      setLocation(DOCUMENTS_ROOT);
    }
  }, [foldersQuery.data, location]);

  const debouncedSearch = useDebounced(search.trim(), 250);
  const isSearching = debouncedSearch.length > 0;
  const spansAreas = isSearching || location.kind === 'recent';

  const acceptKinds = opts.accept?.kinds;
  const effectiveKinds = useMemo<VaultFileKind[]>(() => {
    if (kinds.length > 0) {
      return acceptKinds?.length && !opts.showRejected
        ? kinds.filter((k) => acceptKinds.includes(k))
        : kinds;
    }
    return acceptKinds?.length && !opts.showRejected ? [...acceptKinds] : [];
  }, [kinds, acceptKinds, opts.showRejected]);

  const listingFolderId = spansAreas ? undefined : listingFolderIdOf(location, folders);
  const listingAreas = spansAreas
    ? opts.areas
    : location.kind === 'folder'
      ? [location.area]
      : opts.areas;
  const waitingForArcheSavesRoot =
    !spansAreas &&
    location.kind === 'folder' &&
    location.area === 'arche-saves' &&
    listingFolderId === undefined;

  const listing = useFileDialogListing({
    holderId,
    areas: listingAreas,
    folderId: listingFolderId,
    search: isSearching ? debouncedSearch : undefined,
    kinds: effectiveKinds,
    // Recent is always newest first, whatever the user's sort.
    sort: location.kind === 'recent' && !isSearching ? 'updated' : sort.sort,
    direction: location.kind === 'recent' && !isSearching ? 'desc' : sort.direction,
    forArche: opts.forArche,
    enabled: opts.open && !waitingForArcheSavesRoot && (!!foldersQuery.data || spansAreas),
  });
  const thumbnails = useThumbnails(listing.files);

  const childFolders = useMemo(
    () => (spansAreas ? [] : childFoldersAt(location, folders)),
    [spansAreas, location, folders],
  );

  const entries = useMemo<FileListEntry[]>(() => {
    const folderEntries: FileListEntry[] = childFolders.map((folder) => ({
      kind: 'folder',
      id: folder.folderId,
      name: folder.name,
      folder,
    }));
    const fileEntries: FileListEntry[] = [];
    for (const file of listing.files) {
      const accepted = acceptsFile(opts.accept, file) && (!opts.filter || opts.filter(file));
      if (!accepted && !opts.showRejected) continue;
      fileEntries.push({
        kind: 'file',
        id: file.documentId,
        name: file.name,
        file,
        disabledReason: accepted ? null : 'Not a type this can open',
      });
    }
    return [...folderEntries, ...fileEntries];
  }, [childFolders, listing.files, opts.accept, opts.filter, opts.showRejected]);

  // Read through refs so navigation never runs a side effect inside a state
  // updater (StrictMode runs updaters twice).
  const locationRef = useRef(location);
  locationRef.current = location;
  const historyRef = useRef(history);
  historyRef.current = history;

  const navigate = useCallback((next: FileDialogLocation) => {
    setSearch('');
    const current = locationRef.current;
    if (sameLocation(current, next)) return;
    setHistory([...historyRef.current.slice(-49), current]);
    setLocation(next);
  }, []);

  const back = useCallback(() => {
    const h = historyRef.current;
    if (h.length === 0) return;
    setSearch('');
    setLocation(h[h.length - 1]);
    setHistory(h.slice(0, -1));
  }, []);

  const parent = parentLocationOf(location, folders);
  const up = useCallback(() => {
    if (isSearching) {
      setSearch('');
      return;
    }
    if (parent) navigate(parent);
  }, [isSearching, parent, navigate]);

  const setHolderId = useCallback(
    (next: string) => {
      if (opts.lockedHolderId && next !== opts.lockedHolderId) return;
      setHolderIdState(next);
      setLocation(DOCUMENTS_ROOT);
      setHistory([]);
      setSearch('');
    },
    [opts.lockedHolderId],
  );

  const setView = useCallback((next: FileDialogView) => {
    setViewState(next);
    writeView(next);
  }, []);
  const setSort = useCallback((next: FileDialogSort) => {
    setSortState(next);
    writeSort(next);
  }, []);

  const rememberLocation = useCallback(
    (at?: FileDialogLocation) => {
      const where = at ?? location;
      if (!memory || !holderId || where.kind !== 'folder') return;
      writeLastLocation(memory.userId, memory.archeKey, {
        holderId,
        area: where.area,
        folderId: where.folderId,
      });
    },
    [memory, holderId, location],
  );

  const workspace = workspaces.find((w) => w.holderId === holderId) ?? null;

  return {
    workspaces,
    workspacesLoading,
    workspace,
    holderId,
    setHolderId,
    location,
    navigate,
    back,
    canGoBack: history.length > 0,
    up,
    canGoUp: isSearching || parent !== null,
    folders,
    foldersLoading: foldersQuery.isLoading,
    crumbs: crumbsFor(location, folders),
    search,
    setSearch,
    isSearching,
    kinds,
    setKinds,
    view,
    setView,
    sort,
    setSort,
    listing,
    entries,
    thumbnails,
    showLocation: spansAreas,
    rememberLocation,
    archeSavesMissing: waitingForArcheSavesRoot && !!foldersQuery.data,
  };
}

export type FileBrowser = ReturnType<typeof useFileBrowser>;
